import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChevronIcon from '../../assets/icons/chevron.svg?react';
import { HeaderNavItem, type NavItem } from './HeaderNavItem';
import styles from './HeaderNav.module.css';

// Ties the trigger to its panel for assistive technology.
const PANEL_ID = 'header-nav-more-panel';

interface HeaderNavMoreProps {
  // The pages the bar is too narrow to show, in their usual reading order.
  items: NavItem[];
}

// Holds whatever no longer fits in the bar, so the header never overflows.
// Opens on hover for the mouse and on focus for the keyboard, so the panel is
// never reachable only one way.
export const HeaderNavMore = ({ items }: HeaderNavMoreProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Pointer and focus tracking is bound to the wrapper element directly rather
  // than through JSX handlers: the wrapper is a plain container, not a control,
  // and the interactive element inside it is the trigger button. Hover and
  // focus have to be watched on the whole area so moving the pointer or focus
  // from the trigger into the panel does not close it.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const open = (): void => setIsOpen(true);
    const closeIfFocusLeft = (event: FocusEvent): void => {
      if (!wrapper.contains(event.relatedTarget as Node | null)) setIsOpen(false);
    };

    wrapper.addEventListener('mouseenter', open);
    wrapper.addEventListener('mouseleave', close);
    wrapper.addEventListener('focusin', open);
    wrapper.addEventListener('focusout', closeIfFocusLeft);
    return () => {
      wrapper.removeEventListener('mouseenter', open);
      wrapper.removeEventListener('mouseleave', close);
      wrapper.removeEventListener('focusin', open);
      wrapper.removeEventListener('focusout', closeIfFocusLeft);
    };
  }, [close]);

  // Escape closes while the panel is open. On the document, so it also works
  // when focus sits inside the panel; focus goes back to the trigger, otherwise
  // the keyboard user is dropped at the top of the document.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setIsOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={wrapperRef} className={styles.more}>
      {/* Opens rather than toggles: hovering has already opened the panel by
          the time a click lands, and toggling would shut it on the very click
          the user aimed at the menu. Closing is hover-out, blur or Escape. */}
      <button
        ref={triggerRef}
        type="button"
        className={styles.navLink}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={PANEL_ID}
        onClick={() => setIsOpen(true)}
      >
        {t('nav-more')}
        <ChevronIcon className={styles.moreChevron} aria-hidden="true" />
      </button>
      {isOpen && (
        <ul id={PANEL_ID} className={styles.morePanel}>
          {items.map((item) => (
            <HeaderNavItem
              key={item.labelKey}
              item={item}
              className={styles.moreItem}
              onNavigate={close}
            />
          ))}
        </ul>
      )}
    </div>
  );
};
